import React, { useState, useEffect } from 'react';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import TrimContent from '../../utils/TrimContent';
import PaginationControls from '../../utils/PaginationControls';
import { useTimezone } from '../../utils/TimezoneContext';
import ThemedSelect from '../../utils/ThemedSelect';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TRANSFER_TYPES = [
  { value: '', label: 'All types' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'COMPLETE', label: 'Complete' }
];

// details keys that hold a list of {id, name, actions}, in reading order.
const DETAIL_GROUPS = [
  ['projects', 'Projects'],
  ['data_collections', 'Data Folders'],
];

const ROLE_NAMES = {
  PROJECT_MANAGER: 'Project Manager',
  DOSSIER_DEV_LEAD: 'Dossier Dev Lead',
  DOSSIER_VIEWER: 'Dossier Viewer'
};

const badgesFor = (item) => {
  const actions = item.actions || [];
  const role = ROLE_NAMES[item.target_role_after] || item.target_role_after;
  const out = [];

  if (actions.includes('CREATOR_REASSIGNED')) out.push('Creator');

  if (actions.includes('ROLE_MERGED_PROMOTED')) out.push(`Promoted to ${role}`);
  else if (actions.includes('ROLE_MERGED_KEPT_EXISTING')) out.push(`Kept ${role}`);
  else if (actions.includes('ROLE_MOVED')) out.push(role);

  if (actions.includes('ACCESS_MOVED') || actions.includes('ACCESS_MERGED')) out.push('Access');
  if (actions.includes('MEMBERSHIP_MOVED') || actions.includes('MEMBERSHIP_MERGED')) {
    out.push('Membership');
  }
  return out;
};

const AdminTransferHistory = () => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  const { formatDateTime } = useTimezone();

  const [transfers, setTransfers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [detailTransfer, setDetailTransfer] = useState(null);

  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const API_BASE_URL = '/pceapi/administration';
  const headers = { Authorization: accessToken };

  useEffect(() => {
    fetchTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search, typeFilter, refreshTrigger]);

  const fetchTransfers = async () => {
    setIsProcessingPopupVisible(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/ownership-transfers/list`,
        {
          page,
          size,
          search: search || undefined,
          transfer_type: typeFilter || undefined
        },
        { headers }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load transfer history');
        setIsErrorPopupVisible(true);
        return;
      }

      setTransfers(data.data || []);
      setTotal(data.total || 0);
      setDetailTransfer(null);
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      setPopupErrorMessage(error.message || 'Failed to load transfer history');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const userCell = (email, deleted, name) => (
    <>
      {name ? (
        <>
          <div className="ev-small-p-dark">
            <TrimContent text={name} maxLength={24} />
            {deleted && <span className="ev-message-card1 ev-fs-11 ms-1">deleted</span>}
          </div>
          <div className="ev-fs-11 ev-light-reg">
            <TrimContent text={email} maxLength={28} />
          </div>
        </>
      ) : (
        <>
          <TrimContent text={email} maxLength={28} />
          {deleted && <span className="ev-message-card1 ev-fs-11 ms-1">deleted</span>}
        </>
      )}
    </>
  );

  const renderDetails = (details) => {
    const genbi = details.genbi || {};
    const scopes = details.country_scopes || {};
    const skipped = details.skipped || {};
    const genbiTotal = Object.values(genbi).reduce((a, b) => a + (b || 0), 0);
    const hasGroups = DETAIL_GROUPS.some(([key]) => (details[key] || []).length > 0);
    const notes = [];
    if ((scopes.headships_removed || []).length > 0) {
      notes.push(`${scopes.headships_removed.length} country headship(s) moved`);
    }
    if ((scopes.countries_merged || []).length > 0) {
      notes.push(`${scopes.countries_merged.length} country affiliation(s) added`);
    }

    return (
      <>
        {!hasGroups && notes.length === 0 && (
          <p className="ev-small-p-dark mb-0">This transfer moved nothing.</p>
        )}

        {DETAIL_GROUPS.filter(([key]) => (details[key] || []).length > 0).map(([key, label]) => (
          <div key={key} className="mb-3">
            <label className="ev-reg-p-dark mb-1">
              {label}
              <span className="ev-message-card1 ev-fs-11 ms-2">{details[key].length}</span>
            </label>
            <div className="ev-card-3">
              {details[key].map((item, i) => (
                <div
                  key={item.id}
                  className={`d-flex align-items-center py-1${
                    i < details[key].length - 1 ? ' border-bottom' : ''}`}
                >
                  <span
                    className="ev-small-p-dark avatar-tooltip flex-shrink-0"
                    style={{ width: '55%' }}
                    data-tooltip={item.name || ''}
                  >
                    <TrimContent text={item.name || `#${item.id}`} maxLength={34} />
                  </span>
                  <span className="d-flex flex-wrap gap-1">
                    {badgesFor(item).map(label => (
                      <span key={label} className="ev-message-card1 ev-fs-11">{label}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Counts with no per-row detail worth listing, collapsed into one line. */}
        {notes.length > 0 && (
          <p className="ev-small-p-dark mb-2">
            <i className="bi bi-info-circle me-1"></i>
            {notes.join(' · ')}
          </p>
        )}

        {((skipped.project_ids || []).length > 0 || (skipped.data_collection_ids || []).length > 0) && (
          <p className="text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Skipped {(skipped.project_ids || []).length} project(s) and{' '}
            {(skipped.data_collection_ids || []).length} Data Folder(s) the user did not own
          </p>
        )}
      </>
    );
  };

  return (
    <div className="main-window">
      <AdminTopNav />
      <div className="container-fluid">
        <div className="row">
          <AdminSideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">
                  Transfer History <span className="ev-light-reg">({total})</span>
                </p>
              </div>
              <p className="ev-message-card2 mb-4">
                Completed ownership transfers, newest first.
              </p>

              <div className="ev-card-4 w-100 ev-padding-large">
                {/* Search and filter sit together on the left, Refresh on the
                    right. A Bootstrap column stretched the filter to a third of
                    the toolbar and left it stranded mid-row; it is a short
                    three-option control, so it gets only the width it needs. */}
                <div className="d-flex align-items-center flex-wrap gap-2 mt-1 mb-3">
                  <div className="flex-grow-1" style={{ minWidth: '220px', maxWidth: '420px' }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search by email"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
                      />
                      <button
                        className="ev-btn-lightblue-fill-round search-btn-inside"
                        style={{ left: 0 }}
                        onClick={handleSearch}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                  </div>
                  {/* Filter and Refresh travel together on the right: both act on
                      the table as a whole, where the search box acts on its content. */}
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <div className="themed-select--compact" style={{ width: '160px' }}>
                      <ThemedSelect
                        value={typeFilter}
                        options={TRANSFER_TYPES}
                        ariaLabel="Filter by transfer type"
                        onChange={(next) => { setTypeFilter(next); setPage(1); }}
                      />
                    </div>
                    <button
                      className="ev-btn-lightblue-fill-round ev-bold-reg"
                      onClick={() => setRefreshTrigger(prev => prev + 1)}
                    >
                      <i className="bi bi-arrow-repeat"></i> Refresh
                    </button>
                  </div>
                </div>

                <div className="table-container">
                  <table className="table-custom1 mt-0 overflow-hidden w-100">
                    <thead>
                      <tr>
                        <th className="w-25"><label>From</label></th>
                        <th className="w-25"><label>To</label></th>
                        <th className="w-10"><label>Type</label></th>
                        <th className="w-20"><label>Performed By</label></th>
                        <th className="w-15"><label>When</label></th>
                        <th className="w-5"><label>Details</label></th>
                      </tr>
                    </thead>
                  </table>

                  <div className="scrollable-element dashboard-right-contentarea-table-height-1 pe-1">
                    <table className="table-custom1 overflow-hidden w-100">
                      <tbody>
                        {transfers.length === 0 ? (
                          <div className="w-100 text-center">
                            <p className="ev-small-p-blue mt-5">
                              {isProcessingPopupVisible ? 'Loading transfers' : 'No transfers yet'}
                            </p>
                          </div>
                        ) : (
                          transfers.map((t, idx) => {
                            const tooltipClass = idx === 0 ? 'avatar-tooltip-bottom' : 'avatar-tooltip';
                            return (
                            <tr key={t.id}>
                                <td className={`align-top w-25 ${tooltipClass}`} data-tooltip={t.source_email}>
                                  {userCell(t.source_email, t.source_deleted, t.source_name)}
                                </td>
                                <td className={`align-top w-25 ${tooltipClass}`} data-tooltip={t.target_email}>
                                  {userCell(t.target_email, t.target_deleted, t.target_name)}
                                </td>
                                <td className="align-top w-10">
                                  <span className="ev-message-card1 ev-fs-11">
                                    {t.transfer_type === 'COMPLETE' ? 'Complete' : 'Partial'}
                                  </span>
                                </td>
                                <td className={`align-top w-20 ${tooltipClass}`} data-tooltip={t.performed_by_email || 'Unknown'}>
                                  <div className="ev-message-card5 ev-fs-11">
                                    <TrimContent text={t.performed_by_email || 'Unknown'} maxLength={24} />
                                  </div>
                                </td>
                                <td className="align-top w-15">{formatDateTime(t.performed_at)}</td>
                                <td className="align-top w-5">
                                  <button
                                    type="button"
                                    className={`btn ev-btn-skyblue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 ${tooltipClass}`}
                                    onClick={() => setDetailTransfer(t)}
                                    data-tooltip="View details"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="ev-small-p-dark">
                    Showing {transfers.length} of {total} entries
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="ev-btn-light-fill"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button className="ev-btn-light-fill" disabled>
                      Page {page} of {Math.max(Math.ceil(total / size), 1)}
                    </button>
                    <button
                      className="ev-btn-light-fill"
                      disabled={page >= Math.ceil(total / size)}
                      onClick={() => setPage(page + 1)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                  <div>
                    <PaginationControls
                      initialSize={size}
                      onSizeChange={(newSize) => { setSize(newSize); setPage(1); }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {detailTransfer && (
        <div className="modal fade show d-block bg-dark bg-opacity-50">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
            {/* Shorter than Bootstrap's default scrollable dialog, which grew to
                nearly the full viewport and ran into the bottom edge. Width stays
                at lg: a wider dialog only stretched the gap between each name and
                its badges. */}
            <div className="modal-content" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <div className="modal-header">
                <p className="mb-0 ev-fs-15 ev-bold-med">
                  Transfer Details
                  <span className="ev-message-card1 ev-fs-11 ms-2">
                    {detailTransfer.transfer_type === 'COMPLETE' ? 'Complete' : 'Partial'}
                  </span>
                </p>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDetailTransfer(null)}
                ></button>
              </div>
              <div className="modal-body ev-fs-12 pb-4 scrollable-element" style={{ height: 'auto' }}>
                <div className="ev-card-2 mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span className="ev-small-p-dark">{detailTransfer.source_email}</span>
                    <i className="bi bi-arrow-right"></i>
                    <span className="ev-small-p-dark ev-bold-reg">
                      {detailTransfer.target_email}
                    </span>
                  </div>
                  <span className="ev-fs-11">
                    {formatDateTime(detailTransfer.performed_at)} &middot;{' '}
                    {detailTransfer.performed_by_email || 'Unknown'}
                  </span>
                </div>
                {renderDetails(detailTransfer.details || {})}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary ev-btn-reg"
                  onClick={() => setDetailTransfer(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isProcessingPopupVisible && <ProcessingPopup message="Loading transfer history" />}

      {isErrorPopupVisible && (
        <ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />
      )}
    </div>
  );
};

export default AdminTransferHistory;
